import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { DatePicker } from "antd";
import { AppButton, AppInput, AppSelect } from "components/antd-ui";
import { InputNumber } from "components/Input/InputNumber";
import { memo, useCallback, type ChangeEvent } from "react";
import { OrderDebtStatus } from "types/order";
import dayjs from "utils/dayjs";
import { formatMoney } from "views/Opportunity/opportunity.meta";
import { amountFromPercent, debtStatusOptions } from "../order.meta";

export type DebtRow = {
  key: string;
  id?: number;
  content: string;
  percent: number | null;
  amount: number | null;
  status: OrderDebtStatus;
  dueDate: string;
  collectedDate: string;
};

type OrderDebtsProps = {
  rows: DebtRow[];
  totalAmount: number;
  readOnly?: boolean;
  onChange: (rows: DebtRow[]) => void;
};

const parseNumber = (event: ChangeEvent<HTMLInputElement>) => {
  const raw = event.currentTarget.value.replaceAll(".", "");
  if (raw === "") return null;
  const numeric = Number(raw);
  return Number.isNaN(numeric) ? null : numeric;
};

const toDay = (value: string) => (value ? dayjs(value, "YYYY-MM-DD") : null);

export const createDebtRow = (): DebtRow => ({
  key: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  content: "",
  percent: null,
  amount: null,
  status: OrderDebtStatus.Unpaid,
  dueDate: "",
  collectedDate: "",
});

const DebtItem = memo(
  ({
    row,
    totalAmount,
    readOnly,
    onPatch,
    onRemove,
  }: {
    row: DebtRow;
    totalAmount: number;
    readOnly?: boolean;
    onPatch: (key: string, patch: Partial<DebtRow>) => void;
    onRemove: (key: string) => void;
  }) => {
    const handleContentChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onPatch(row.key, { content: event.target.value });
      },
      [onPatch, row.key],
    );

    const handlePercentChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const percent = parseNumber(event);
        onPatch(row.key, {
          percent,
          amount: percent === null ? row.amount : amountFromPercent(totalAmount, percent),
        });
      },
      [onPatch, row.amount, row.key, totalAmount],
    );

    const handleAmountChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onPatch(row.key, { amount: parseNumber(event) });
      },
      [onPatch, row.key],
    );

    const handleStatusChange = useCallback(
      (value: OrderDebtStatus) => {
        onPatch(row.key, { status: value });
      },
      [onPatch, row.key],
    );

    const handleDueChange = useCallback(
      (value: dayjs.Dayjs | null) => {
        onPatch(row.key, { dueDate: value ? value.format("YYYY-MM-DD") : "" });
      },
      [onPatch, row.key],
    );

    const handleCollectedChange = useCallback(
      (value: dayjs.Dayjs | null) => {
        onPatch(row.key, { collectedDate: value ? value.format("YYYY-MM-DD") : "" });
      },
      [onPatch, row.key],
    );

    const remove = useCallback(() => onRemove(row.key), [onRemove, row.key]);

    return (
      <div className="orderDebts__row">
        <AppInput
          value={row.content}
          onChange={handleContentChange}
          disabled={readOnly}
          placeholder="Nội dung đợt thu"
        />
        <InputNumber value={row.percent ?? ""} onChange={handlePercentChange} disabled={readOnly} />
        <InputNumber value={row.amount ?? ""} onChange={handleAmountChange} disabled={readOnly} />
        <AppSelect
          value={row.status}
          options={debtStatusOptions}
          onChange={handleStatusChange}
          disabled={readOnly}
        />
        <DatePicker
          className="orderDebts__date"
          value={toDay(row.dueDate)}
          format="DD/MM/YYYY"
          placeholder="Hạn thu"
          onChange={handleDueChange}
          disabled={readOnly}
          allowClear
        />
        <DatePicker
          className="orderDebts__date"
          value={toDay(row.collectedDate)}
          format="DD/MM/YYYY"
          placeholder="Ngày thu"
          onChange={handleCollectedChange}
          disabled={readOnly}
          allowClear
        />
        {!readOnly && (
          <button type="button" className="orderDebts__remove" onClick={remove} aria-label="Xóa đợt thu">
            <DeleteOutlined />
          </button>
        )}
      </div>
    );
  },
);

DebtItem.displayName = "DebtItem";

export const OrderDebts = memo(({ rows, totalAmount, readOnly, onChange }: OrderDebtsProps) => {
  const onPatch = useCallback(
    (key: string, patch: Partial<DebtRow>) => {
      onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    },
    [onChange, rows],
  );

  const onRemove = useCallback(
    (key: string) => {
      onChange(rows.filter((row) => row.key !== key));
    },
    [onChange, rows],
  );

  const addRow = useCallback(() => {
    onChange([...rows, createDebtRow()]);
  }, [onChange, rows]);

  const debtTotal = rows.reduce((sum, row) => sum + (row.amount || 0), 0);

  return (
    <div className="orderDebts">
      <div className="orderDebts__head">
        <span>Nội dung</span>
        <span>%</span>
        <span>Số tiền</span>
        <span>Trạng thái</span>
        <span>Hạn thu</span>
        <span>Ngày thu</span>
        <span />
      </div>
      {rows.map((row) => (
        <DebtItem
          key={row.key}
          row={row}
          totalAmount={totalAmount}
          readOnly={readOnly}
          onPatch={onPatch}
          onRemove={onRemove}
        />
      ))}
      {!readOnly && (
        <AppButton className="productTable__add" icon={<PlusOutlined />} onClick={addRow}>
          Thêm đợt thu
        </AppButton>
      )}
      <div className="orderForm__totals">
        <span>Tổng hàng hóa: {formatMoney(totalAmount)}</span>
        <span>Tổng các đợt: {formatMoney(debtTotal)}</span>
      </div>
    </div>
  );
});

OrderDebts.displayName = "OrderDebts";
